import { motion, useAnimation } from "motion/react"
import { useEffect, useRef, useState } from "react"
import WebsiteWireframe from "../WebsiteWireframe"

interface ScrollingScanScreenshotProps {
    imageUrl?: string
    className?: string
}



export default function ScrollingScanScreenshot({ imageUrl, className }: ScrollingScanScreenshotProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null) // Ref for the motion.div containing the image
    const controls = useAnimation()
    const [dimensionsReady, setDimensionsReady] = useState(false)

    const [resolvedImage, setResolvedImage] = useState<string | null>(null)
    const [isCheckingImage, setIsCheckingImage] = useState<boolean>(true)

    useEffect(() => {
        let isCancelled = false

        const preloadImage = (url: string, timeoutMs = 10000): Promise<boolean> => {
            return new Promise((resolve) => {
                const img = new Image()
                let settled = false
                const done = (ok: boolean) => {
                    if (settled) return
                    settled = true
                    img.onload = null
                    img.onerror = null
                    clearTimeout(timer)
                    resolve(ok)
                }
                const timer = setTimeout(() => done(false), timeoutMs)
                img.onload = () => done(true)
                img.onerror = () => done(false)
                img.src = url
            })
        }

        const headOk = async (url: string): Promise<boolean | null> => {
            try {
                const res = await fetch(url, { method: 'HEAD', cache: 'no-cache' })
                return res.ok
            } catch {
                return null
            }
        }

        const isImageUsable = async (url: string): Promise<boolean> => {
            if (!url) return false
            if (url.startsWith('blob:') || url.startsWith('data:')) {
                return await preloadImage(url)
            }
            const ok = await headOk(url)
            if (ok === false) return false
            return await preloadImage(url)
        }

        const resolve = async () => {
            setIsCheckingImage(true)
            setResolvedImage(null)
            try {
                if (imageUrl) {
                    const usable = await isImageUsable(imageUrl)
                    if (usable && !isCancelled) {
                        setResolvedImage(imageUrl)
                    }
                }
            } finally {
                if (!isCancelled) setIsCheckingImage(false)
            }
        }

        resolve()
        return () => {
            isCancelled = true
        }
    }, [imageUrl])


    useEffect(() => {
        const measureDimensions = () => {
            if (containerRef.current && contentRef.current) {
                setDimensionsReady(true)
            }
        }

        const timeoutId = setTimeout(measureDimensions, 100)
        window.addEventListener("resize", measureDimensions)

        return () => {
            clearTimeout(timeoutId)
            window.removeEventListener("resize", measureDimensions)
        }
    }, [resolvedImage])

    useEffect(() => {
        const animateScroll = async () => {
            if (!containerRef.current || !contentRef.current || !dimensionsReady || !resolvedImage) {
                return
            }

            // Add initial 4-second delay
            await new Promise((resolve) => setTimeout(resolve, 4000))

            const containerHeight = containerRef.current.clientHeight
            const contentHeight = contentRef.current.clientHeight // This is the rendered height of the image
            const scrollableHeight = contentHeight - containerHeight

            if (scrollableHeight <= 0) {
                return
            }

            let currentScrollY = 0
            const scrollStep = containerHeight // Scroll by the height of the parent container

            const numSteps = Math.ceil(scrollableHeight / scrollStep)

            for (let i = 0; i < numSteps; i++) {
                const targetY = Math.min(currentScrollY + scrollStep, scrollableHeight)
                const duration = 2 // seconds

                await controls.start({
                    y: -targetY, // Negative Y to scroll upwards
                    transition: { duration: duration, ease: "easeInOut" },
                })

                currentScrollY = targetY

                if (currentScrollY < scrollableHeight) {
                    await new Promise((resolve) => setTimeout(resolve, 4000)) // Pause for 4 seconds
                }
            }
        }

        if (dimensionsReady) {
            animateScroll()
        }
    }, [dimensionsReady, controls, resolvedImage])

    return (
        <div
            ref={containerRef}
            className="w-full max-w-md aspect-video relative overflow-hidden rounded-lg shadow-lg bg-gray-200 dark:bg-gray-800"
        >
            {isCheckingImage ? (
                <div className="absolute inset-0 w-full h-full">
                    <WebsiteWireframe />
                </div>
            ) : resolvedImage ? (
                <motion.div
                    ref={contentRef}
                    className="absolute top-0 left-0 w-full"
                    animate={controls}
                >
                    <img src={resolvedImage} alt="" className={className} />
                </motion.div>
            ) : (
                <div className="absolute inset-0 w-full h-full">
                    <WebsiteWireframe />
                </div>
            )}
        </div>
    )
}